import classnames from 'classnames';
import React, { useState, Ref, useEffect, KeyboardEvent } from 'react';

import { DangerIcon } from '@components/Icons';

import { amountInPound } from '@utils/currencyFormatter';

import styles from './CurrencyInput.module.scss';

export type CurrencyInputProps = {
  value?: number;
  defaultValue?: number;
  label?: string;
  prefix?: string;
  disabled?: boolean;
  errorMessage?: string;
  invalidTotal?: boolean;
  manualValidation?: boolean;
  name?: string;
  className?: string;
  id?: string;
  testId?: string;
  limit?: number;
  onChange?: (value: number) => void;
  ref?: Ref<HTMLInputElement>;
};

export const CurrencyInput = ({
  label,
  value = 0,
  defaultValue = 0,
  prefix = '£',
  disabled,
  errorMessage,
  invalidTotal = false,
  manualValidation = false,
  name,
  className,
  testId,
  limit,
  onChange,
  ref,
}: CurrencyInputProps) => {
  const [valueInPence, setValueInPence] = useState(defaultValue || 0);
  const [errorState, setErrorState] = useState(false);

  const handleOnKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const isNumericKey = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].includes(event.key);
    let inputValue = `${valueInPence}`;

    const isBackspaceKey = event.key === 'Backspace' && valueInPence > 0;
    const isDeleteKey = event.key === 'Delete';

    if (limit && +valueInPence >= limit && isNumericKey) {
      event.preventDefault();
      return;
    }

    if (isNumericKey) {
      inputValue = inputValue === '0' ? event.key : inputValue + event.key;
    } else if (isBackspaceKey) {
      inputValue = inputValue.slice(0, -1);
    } else if (isDeleteKey) {
      inputValue = '';
    }

    const newValue = +inputValue;
    const isOverLimit = limit && newValue > limit;

    if (isOverLimit) {
      setErrorState(true);
    } else {
      setErrorState(false);
    }

    if (newValue !== valueInPence) {
      setValueInPence(newValue);
      onChange?.(newValue);
    }
  };

  useEffect(() => {
    if (value !== valueInPence) {
      setValueInPence(+value);
    }
  }, [value]);

  const inputError = !!errorMessage || invalidTotal;

  useEffect(() => {
    if (limit !== undefined && valueInPence > limit) {
      setErrorState(true);
    } else {
      setErrorState(false);
    }
  }, [limit, valueInPence]);

  const inputBoxClasses = classnames(styles.inputBox, {
    [styles.error]: !manualValidation && (!!errorMessage || errorState || inputError),
    [styles.disabled]: disabled && !(!manualValidation && inputError),
  });

  return (
    <div>
      <div className={classnames(styles.container, className)}>
        {label && (
          <label htmlFor={name} className={styles.label}>
            {label}
          </label>
        )}
        {errorMessage && (
          <div className={styles.errorMessage} id={`${name}-error`}>
            <span className={styles.errorText}>{errorMessage}</span>
            <span className={classnames(styles.warningIcon)}>
              <DangerIcon size={18} />
            </span>
          </div>
        )}
        <div className={inputBoxClasses}>
          <div className={styles.prefix}>
            <div className={styles.prefixContent}>{prefix}</div>
          </div>
          <input
            id={name}
            name={name}
            className={styles.input}
            type="text"
            value={amountInPound(valueInPence)}
            onChange={() => {}}
            onKeyDown={handleOnKeyDown}
            disabled={disabled}
            aria-invalid={!!errorMessage}
            aria-describedby={`${name}-error`}
            data-testid={testId}
            ref={ref}
          />
        </div>
      </div>
    </div>
  );
};
